/**
 * PreparationSession — guided step-by-step experience for a single preparation.
 *
 * Route: /preparation/:id
 * No Claude calls — these are structured, timer-based flows with Sage's scripts.
 * Steps with durationSeconds > 0 auto-advance with a progress bar.
 * Steps with durationSeconds === 0 wait for a tap (no timer).
 *
 * The affirmations preparation uses a distinct card-based rendering mode:
 *   - Heart-save icon on each saveable card (local state)
 *   - Tier gate: free users see Emotional + Body cards only (freeTierStepCount = 4)
 *   - Upsell screen after free-tier limit
 *   - Breath-prompt interstitial before Inner Child category
 *   - "Add to Inner Sanctuary" saves to insight_cards on completion (Practice+)
 *   - Attribution footer on every affirmation screen
 */
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import type { User } from '@supabase/supabase-js'
import { pluralCategory } from '../lib/plural'
import { getPreparation } from '../data/preparations'
import { localizePreparation } from '../data/preparations.pl'
import type { Preparation } from '../data/preparations'
import { db } from '../lib/supabase'

interface PreparationSessionProps {
  user?: User
}

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M12.5 15l-5-5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

/** Animated circular progress ring for timed steps */
function ProgressRing({
  progress,
  color,
  size = 72,
}: {
  progress: number  // 0–1
  color: string
  size?: number
}) {
  const radius = (size - 8) / 2
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference * (1 - progress)

  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      {/* Track */}
      <circle
        cx={size / 2} cy={size / 2} r={radius}
        fill="none"
        stroke="#E4D5EE"
        strokeWidth="4"
      />
      {/* Progress arc */}
      <circle
        cx={size / 2} cy={size / 2} r={radius}
        fill="none"
        stroke={color}
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        style={{ transition: 'stroke-dashoffset 0.5s ease' }}
      />
    </svg>
  )
}

/** Heart icon — filled when saved, outline when not */
function HeartIcon({ filled, color }: { filled: boolean; color: string }) {
  return (
    <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
      <path
        d="M10 17.5S2.5 12.5 2.5 7.5a4.5 4.5 0 0 1 7.5-3.354A4.5 4.5 0 0 1 17.5 7.5C17.5 12.5 10 17.5 10 17.5z"
        fill={filled ? color : 'none'}
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export default function PreparationSession({ user }: PreparationSessionProps) {
  const { t, i18n } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  // Swap in the reader's language once, here, so every step, cue and closing
  // line below reads from the same localized practice. Timings and the
  // free-tier gate pass through untouched (see localizePreparation).
  const rawPrep: Preparation | undefined = id ? getPreparation(id as Preparation['id']) : undefined
  const prep: Preparation | undefined = rawPrep ? localizePreparation(rawPrep, i18n.language) : undefined

  const [stepIndex, setStepIndex] = useState(0)
  const [elapsed, setElapsed] = useState(0)
  const [done, setDone] = useState(false)

  // Affirmations-specific state — only loaded when prep.isAffirmations
  const [tier, setTier] = useState<string | null>(prep?.isAffirmations ? null : 'free')
  const [savedIndices, setSavedIndices] = useState<Set<number>>(new Set())
  const [showUpsell, setShowUpsell] = useState(false)
  const [journalSaved, setJournalSaved] = useState(false)
  const [savingJournal, setSavingJournal] = useState(false)

  // Timer — ticks every 500ms for smooth progress ring
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const currentStep = prep?.steps[stepIndex]
  const isTimed = (currentStep?.durationSeconds ?? 0) > 0
  const progress = isTimed ? Math.min(elapsed / (currentStep!.durationSeconds * 2), 1) : 0

  // Load subscription tier for affirmations — determines step count and save eligibility
  useEffect(() => {
    if (!prep?.isAffirmations) return
    if (!user) { setTier('free'); return }
    Promise.resolve(
      db().from('profiles')
        .select('subscription_tier')
        .eq('id', user.id)
        .single()
    ).then(({ data }) => setTier(data?.subscription_tier ?? 'free'))
      .catch(() => setTier('free'))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, prep?.id])

  // Timer — start/stop based on step
  useEffect(() => {
    if (!isTimed) return

    setElapsed(0)
    intervalRef.current = setInterval(() => {
      setElapsed(prev => prev + 1)
    }, 500)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex])

  // Auto-advance when timer completes
  useEffect(() => {
    if (!isTimed || !currentStep) return
    if (elapsed >= currentStep.durationSeconds * 2) {
      advance()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elapsed])

  function advance() {
    if (!prep) return
    if (intervalRef.current) clearInterval(intervalRef.current)

    const nextIndex = stepIndex + 1

    // Free-tier limit for affirmations — show upsell instead of continuing
    if (
      prep.isAffirmations &&
      prep.freeTierStepCount !== undefined &&
      tier === 'free' &&
      nextIndex >= prep.freeTierStepCount
    ) {
      setShowUpsell(true)
      return
    }

    if (stepIndex < prep.steps.length - 1) {
      setStepIndex(s => s + 1)
      setElapsed(0)
    } else {
      setDone(true)
    }
  }

  /** Toggle saved state for an affirmation card */
  function toggleSave(index: number) {
    setSavedIndices(prev => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  /** Write saved affirmations to insight_cards — Practice+ only */
  async function saveToJournal() {
    if (!user || journalSaved || savingJournal || !prep) return
    const toSave = prep.steps
      .map((step, i) => ({ step, i }))
      .filter(({ step, i }) => step.isSaveable && savedIndices.has(i))
    if (toSave.length === 0) return

    setSavingJournal(true)
    try {
      await db().from('insight_cards').insert(
        toSave.map(({ step }) => ({
          user_id: user.id,
          session_id: null,
          card_type: 'affirmation',
          card_title: step.category ?? 'Affirmation',
          card_message: step.instruction,
          saved: true,
        }))
      )
      setJournalSaved(true)
    } catch (err) {
      console.error('Failed to save affirmations to journal:', err)
    } finally {
      setSavingJournal(false)
    }
  }

  // Unknown preparation id — redirect
  if (!prep) {
    navigate('/preparations', { replace: true })
    return null
  }

  // Loading tier for affirmations — brief spinner before first card is shown
  if (tier === null) {
    return (
      <div className="min-h-svh flex items-center justify-center" style={{ background: '#FAF7F4' }}>
        <div className="w-5 h-5 rounded-full border-2 border-[#9B72C0] border-t-transparent animate-spin" />
      </div>
    )
  }

  // ── Upsell screen — free-tier users after freeTierStepCount cards ─────────
  if (showUpsell) {
    return (
      <div
        className="min-h-svh flex flex-col items-center justify-center px-6 page-enter"
        style={{ background: '#FAF7F4' }}
      >
        <div className="w-full max-w-sm text-center">
          <div
            className="w-14 h-14 rounded-full mx-auto mb-8 flex items-center justify-center"
            style={{ background: prep.colorSoft, border: `1px solid ${prep.color}30` }}
          >
            <div style={{ width: 14, height: 14, borderRadius: '50%', background: prep.color, opacity: 0.7 }} />
          </div>

          <p
            className="font-display text-xl leading-snug mb-3 px-4"
            style={{ color: '#1C1128', fontWeight: 400, fontStyle: 'italic' }}
          >
            {t('prep.movedThrough')}
          </p>

          <p className="text-sm leading-relaxed mb-8" style={{ color: '#9B8FAA' }}>
            {t('prep.restAvailable')}
          </p>

          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/profile')}
              className="w-full py-4 rounded-xl font-medium tracking-wide"
              style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
            >
              {t('prep.unlockPractice')}
            </button>
            <button
              onClick={() => { setShowUpsell(false); setDone(true) }}
              className="w-full py-3 rounded-xl text-sm"
              style={{ background: 'transparent', border: '1.5px solid #E4D5EE', color: '#6B5F7A', cursor: 'pointer' }}
            >
              {t('prep.completeHere')}
            </button>
          </div>

          {prep.attribution && (
            <p className="text-xs mt-10" style={{ color: '#C4B5D4', fontStyle: 'italic' }}>
              {prep.attribution}
            </p>
          )}
        </div>
      </div>
    )
  }

  // ── Completion screen ─────────────────────────────────────────────────────
  const isPracticePlus = tier !== 'free'
  const savedCount = [...savedIndices].filter(i => prep.steps[i]?.isSaveable).length

  if (done) {
    return (
      <div
        className="min-h-svh flex flex-col items-center justify-center px-6 page-enter"
        style={{ background: '#FAF7F4' }}
      >
        <div className="w-full max-w-sm text-center">
          {/* Completion glow */}
          <div
            className="w-16 h-16 rounded-full mx-auto mb-8 flex items-center justify-center"
            style={{
              background: `radial-gradient(circle, ${prep.colorSoft} 0%, transparent 70%)`,
              border: `1px solid ${prep.color}30`,
            }}
          >
            <div style={{ width: 16, height: 16, borderRadius: '50%', background: prep.color, opacity: 0.8 }} />
          </div>

          {/* Completion line */}
          <p
            className="font-display text-xl leading-snug mb-3 px-4"
            style={{ color: '#1C1128', fontWeight: 400, fontStyle: 'italic' }}
          >
            {prep.completionLine}
          </p>

          <p className="text-sm mb-2" style={{ color: '#9B8FAA' }}>
            {t('prep.completeIn', { name: prep.name, min: prep.durationMinutes })}
          </p>

          {/* Attribution — shown for all affirmations completions */}
          {prep.attribution && (
            <p className="text-xs mb-8" style={{ color: '#C4B5D4', fontStyle: 'italic' }}>
              {prep.attribution}
            </p>
          )}

          {/* Save to Sanctuary — Practice+ only, requires at least one saved card */}
          {prep.isAffirmations && isPracticePlus && savedCount > 0 && (
            <div
              className="mb-6 px-4 py-4 rounded-xl"
              style={{ background: prep.colorSoft, border: `1px solid ${prep.color}20` }}
            >
              {journalSaved ? (
                <p className="text-sm" style={{ color: prep.color }}>
                  {t(`prep.affirmationsSaved.${pluralCategory(savedCount, i18n.language)}`, { count: savedCount })}
                </p>
              ) : (
                <>
                  <p className="text-sm mb-3" style={{ color: '#6B5F7A' }}>
                    {t(`prep.youSaved.${pluralCategory(savedCount, i18n.language)}`, { count: savedCount })}
                  </p>
                  <button
                    onClick={saveToJournal}
                    disabled={savingJournal}
                    className="w-full py-2.5 rounded-lg text-sm font-medium transition-opacity"
                    style={{
                      background: prep.color,
                      color: 'white',
                      border: 'none',
                      cursor: savingJournal ? 'wait' : 'pointer',
                      opacity: savingJournal ? 0.7 : 1,
                    }}
                  >
                    {savingJournal ? t('common.saving') : t('prep.addToSanctuary')}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/session', { state: { fresh: true } })}
              className="w-full py-4 rounded-xl font-medium tracking-wide"
              style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
            >
              {t('prep.beginASession')}
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full py-3 rounded-xl text-sm"
              style={{ background: 'transparent', border: '1.5px solid #E4D5EE', color: '#6B5F7A', cursor: 'pointer' }}
            >
              {t('prep.returnHome')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Step screen ───────────────────────────────────────────────────────────
  const stepNumber = stepIndex + 1
  // Free-tier users see a shorter dot trail capped at freeTierStepCount
  const totalSteps =
    tier === 'free' && prep.freeTierStepCount !== undefined
      ? prep.freeTierStepCount
      : prep.steps.length

  const isBreathPrompt = prep.isAffirmations && !!currentStep?.isBreathPrompt
  const isAffirmationCard = prep.isAffirmations && !isBreathPrompt
  const isSaved = savedIndices.has(stepIndex)
  // "Complete" label only on the very last step of the full set (Practice+ only reach it)
  const isLastStep = stepIndex === prep.steps.length - 1

  return (
    <div
      className="min-h-svh flex flex-col"
      style={{ background: '#FAF7F4' }}
    >
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-5 max-w-xl mx-auto w-full">
        <button
          onClick={() => navigate('/preparations')}
          className="hover:opacity-70 transition-opacity"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9B8FAA' }}
          aria-label={t('prep.exitPreparation')}
        >
          <BackIcon />
        </button>

        {/* Progress dots */}
        <div className="flex items-center gap-1.5">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div
              key={i}
              className="rounded-full transition-all"
              style={{
                width: i === stepIndex ? 8 : 5,
                height: i === stepIndex ? 8 : 5,
                background: i <= stepIndex ? prep.color : '#E4D5EE',
              }}
            />
          ))}
        </div>

        <span className="text-xs" style={{ color: '#C4B5D4' }}>
          {stepNumber} of {totalSteps}
        </span>
      </header>

      {/* Main step content */}
      <main className="flex-1 flex flex-col items-center justify-center px-8 pb-24 page-enter">
        <div className="w-full max-w-sm text-center">

          {/* ── Affirmation card rendering ──────────────────────────────────── */}
          {isAffirmationCard && (
            <>
              {/* Category label + heart save toggle */}
              <div className="flex items-center justify-between mb-5">
                <p
                  className="text-xs font-medium uppercase tracking-widest"
                  style={{ color: prep.color }}
                >
                  {currentStep!.category ?? prep.name}
                </p>
                {currentStep!.isSaveable && isPracticePlus && (
                  <button
                    onClick={() => toggleSave(stepIndex)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                    aria-label={isSaved ? t('prep.removeFromSaved') : t('prep.saveAffirmation')}
                  >
                    <HeartIcon filled={isSaved} color={prep.color} />
                  </button>
                )}
              </div>

              {/* Affirmation text */}
              <p
                className="font-display text-2xl leading-snug mb-4 message-appear"
                style={{ color: '#1C1128', fontWeight: 400, fontStyle: 'italic' }}
                key={stepIndex}
              >
                {currentStep!.instruction}
              </p>

              <button
                onClick={advance}
                className="w-full py-4 rounded-xl font-medium tracking-wide mt-8 transition-all"
                style={{ background: prep.color, color: 'white', border: 'none', cursor: 'pointer' }}
              >
                {stepIndex === 0 ? t('common.begin') : isLastStep ? t('common.complete') : t('common.continue')}
              </button>
            </>
          )}

          {/* ── Breath prompt interstitial ──────────────────────────────────── */}
          {isBreathPrompt && (
            <>
              <div
                className="inline-block px-6 py-5 rounded-2xl mb-8"
                style={{ background: prep.colorSoft, border: `1px solid ${prep.color}20` }}
              >
                <p
                  className="font-display text-lg leading-relaxed"
                  style={{ color: '#6B5F7A', fontWeight: 400, fontStyle: 'italic' }}
                >
                  {currentStep!.instruction}
                </p>
                {currentStep!.cue && (
                  <p className="text-xs mt-2" style={{ color: '#9B8FAA' }}>
                    {currentStep!.cue}
                  </p>
                )}
              </div>

              <button
                onClick={advance}
                className="w-full py-4 rounded-xl font-medium tracking-wide transition-all"
                style={{ background: prep.color, color: 'white', border: 'none', cursor: 'pointer' }}
              >
                {t('session.continue')}
              </button>
            </>
          )}

          {/* ── Standard preparation step rendering ────────────────────────── */}
          {!prep.isAffirmations && (
            <>
              {/* Supertitle */}
              <p
                className="text-xs font-medium uppercase tracking-widest mb-6"
                style={{ color: prep.color }}
              >
                {prep.name}
              </p>

              {/* Timer ring — only for timed steps */}
              {isTimed && (
                <div className="flex justify-center mb-8">
                  {/* Relative wrapper so the countdown number can be centered over the ring */}
                  <div className="relative inline-flex items-center justify-center">
                    <ProgressRing progress={progress} color={prep.color} size={80} />
                    <div
                      className="absolute flex flex-col items-center"
                      style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
                    >
                      <p className="text-xl font-mono leading-none" style={{ color: prep.color }}>
                        {Math.max(0, Math.ceil((currentStep!.durationSeconds * 2 - elapsed) / 2))}
                      </p>
                      <p className="text-xs leading-none mt-0.5" style={{ color: '#9B8FAA' }}>sec</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Step instruction */}
              <p
                className="font-display text-2xl leading-snug mb-4 message-appear"
                style={{ color: '#1C1128', fontWeight: 400, fontStyle: 'italic' }}
                key={stepIndex}
              >
                {currentStep!.instruction}
              </p>

              {/* Cue text */}
              {currentStep!.cue && (
                <p className="text-sm leading-relaxed mb-10" style={{ color: '#9B8FAA' }}>
                  {currentStep!.cue}
                </p>
              )}

              {/* Tap-to-advance — only for non-timed steps */}
              {!isTimed && (
                <button
                  onClick={advance}
                  className="w-full py-4 rounded-xl font-medium tracking-wide mt-6 transition-all"
                  style={{ background: prep.color, color: 'white', border: 'none', cursor: 'pointer' }}
                >
                  {stepIndex === 0 ? t('common.begin') : t('common.continue')}
                </button>
              )}

              {/* Timed steps — skip button as secondary option */}
              {isTimed && (
                <button
                  onClick={advance}
                  className="text-xs mt-8"
                  style={{ background: 'none', border: 'none', color: '#C4B5D4', cursor: 'pointer' }}
                >
                  {t('common.skip')}
                </button>
              )}
            </>
          )}
        </div>
      </main>

      {/* Attribution footer — shown on every affirmation step */}
      {prep.attribution && (
        <footer className="pb-6 text-center">
          <p className="text-xs" style={{ color: '#C4B5D4', fontStyle: 'italic' }}>
            {prep.attribution}
          </p>
        </footer>
      )}
    </div>
  )
}
